import React, { useMemo } from "react";
import { Cell, Pie, PieChart, Tooltip } from "recharts";
import { formatHours, pluralize } from "./cardFormat";
import TruncatedText from "../truncatedText/TruncatedText";
import styles from "./resourceInsights.module.scss";

/** The colours of the slices: the brand's purples first, then colours that stay apart from them. */
const COLORS = ["#342c82", "#8f86d6", "#2f9e8f", "#e0a030", "#d6637a", "#564f97", "#bdb7ec"];
const OTHER_COLOR = "#b8b8c8";

/** Projects shown one by one; the smallest of the rest are put together. */
const MAX_PROJECTS = COLORS.length;

const SIZE = 150;

/** The projects that have hours, largest first, with every one beyond the last colour put together. */
function toSlices(byProject) {
  const projects = byProject
    .filter((project) => project.allocatedHours > 0)
    .sort((a, b) => b.allocatedHours - a.allocatedHours)
    .map((project, index) => ({ projectId: project.projectId, name: project.name, hours: project.allocatedHours, color: COLORS[index % COLORS.length] }));

  if (projects.length <= MAX_PROJECTS) return projects;

  const rest = projects.slice(MAX_PROJECTS - 1);
  return [
    ...projects.slice(0, MAX_PROJECTS - 1),
    { projectId: null, name: `Other (${pluralize(rest.length, "project")})`, hours: rest.reduce((sum, project) => sum + project.hours, 0), color: OTHER_COLOR },
  ];
}

/**
 * Where the hours of the period go, as a donut with the total in the middle and a legend that names each project with
 * its hours and share. A project in the legend opens its tasks when tasks can be opened.
 *
 * @param byProject  `[{ projectId, name, allocatedHours }]` of the period
 * @param onOpenProject  `(projectId)`, asks for the tasks of one project
 */
export default function AllocationDonut({ byProject, canOpenTasks, onOpenProject }) {
  const slices = useMemo(() => toSlices(byProject), [byProject]);
  const total = slices.reduce((sum, slice) => sum + slice.hours, 0);

  if (slices.length === 0) return <p className={styles.note}>No hours are allocated to a project in this period.</p>;

  return (
    <div className={styles.donutBlock}>
      <div className={styles.donut}>
        <PieChart width={SIZE} height={SIZE}>
          <Pie data={slices} dataKey="hours" nameKey="name" innerRadius={50} outerRadius={72} paddingAngle={slices.length > 1 ? 2 : 0} stroke="none" startAngle={90} endAngle={-270}>
            {slices.map((slice) => (
              <Cell key={slice.name} fill={slice.color} />
            ))}
          </Pie>
          <Tooltip formatter={(value, name) => [formatHours(value), name]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
        </PieChart>

        <div className={styles.donutCenter}>
          <strong>{formatHours(total)}</strong>
          <span>allocated</span>
        </div>
      </div>

      <div className={styles.legend}>
        {slices.map((slice) => {
          const content = (
            <>
              <span className={styles.legendDot} style={{ background: slice.color }} />
              <TruncatedText className={styles.legendName} text={slice.name} />
              <span className={styles.legendValue}>
                {formatHours(slice.hours)}
                <em>{Math.round((slice.hours / total) * 100)}%</em>
              </span>
            </>
          );

          return canOpenTasks && slice.projectId ? (
            <button key={slice.name} type="button" className={styles.legendRow} onClick={() => onOpenProject(slice.projectId)}>
              {content}
            </button>
          ) : (
            <div key={slice.name} className={styles.legendRow}>
              {content}
            </div>
          );
        })}
      </div>
    </div>
  );
}
